import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { usePasswordResetRequest } from '@/lib/auth';
import { MultiUserDisabledError, RateLimitedError } from '@/lib/api';

/**
 * Solicita un email de password reset. Por seguridad (no enumeration), el
 * mensaje de éxito es genérico — siempre dice "si el email existe, recibirás
 * un enlace" aunque el email no esté registrado.
 *
 * En la instalación self-hosted no hay servidor SMTP por defecto: los enlaces
 * se loggean en `logs/reset-tokens/` para que el admin los recupere a mano.
 * Lo decimos explícitamente en el mensaje para que el usuario no se quede
 * esperando un email que no llegará.
 */
export function PasswordResetRequestForm(): ReactElement {
  const [email, setEmail] = useState('');
  const mutation = usePasswordResetRequest();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || mutation.isPending) return;
    mutation.mutate({ email: email.trim() });
  }

  function errorMessage(): string | null {
    const err = mutation.error;
    if (!err) return null;
    if (err instanceof MultiUserDisabledError) {
      return 'Esta instalación no permite reset por email.';
    }
    if (err instanceof RateLimitedError) {
      const seconds = err.retryAfterSeconds;
      if (seconds === null) return 'Demasiados intentos. Espera un poco.';
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Demasiados intentos. Espera ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
    }
    return 'No se pudo procesar la solicitud. Inténtalo más tarde.';
  }

  const err = errorMessage();

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Recuperar contraseña</h2>
        <p className="mt-1 text-sm text-slate-600">
          Introduce el email asociado a tu cuenta.
        </p>
      </header>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-700">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending || mutation.isSuccess}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      {err && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {err}
        </p>
      )}

      {mutation.isSuccess && (
        <p
          role="status"
          className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          Si el email existe, recibirás un enlace de recuperación. (En esta
          instalación los enlaces se guardan en <code>logs/reset-tokens/</code>{' '}
          para recuperación manual.)
        </p>
      )}

      <button
        type="submit"
        disabled={mutation.isPending || mutation.isSuccess || email.length === 0}
        aria-busy={mutation.isPending}
        className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {mutation.isPending ? 'Enviando…' : 'Enviar enlace'}
      </button>

      <p className="text-sm text-slate-600">
        <Link
          to="/login"
          className="text-teal-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Volver al login
        </Link>
      </p>
    </form>
  );
}
